import React from 'react';

/**
 * ResourceBars - Displays molecule pools and damage trackers as animated bars.
 */
const ResourceBars = ({ simState, scenario, advancedMode }) => {
  if (!simState) return null;

  // Resources to show
  const resourceMolecules = ['GSH', 'PAPS', 'UDPGA'];
  const toxicMolecules = ['NAPQI', 'APAP'];

  const getName = (item) => {
    if (!advancedMode && item.simpleName) return item.simpleName;
    return item.name;
  };

  const renderBar = (label, value, max, color, warning = false, danger = false) => {
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    const barClass = danger ? 'bar-danger' : warning ? 'bar-warning' : '';

    return (
      <div className={`resource-bar ${barClass}`} key={label}>
        <div className="resource-bar-label">
          <span>{label}</span>
          <span className="resource-bar-value">{value.toFixed(1)} / {max}</span>
        </div>
        <div className="resource-bar-track">
          <div
            className="resource-bar-fill"
            style={{
              width: `${pct}%`,
              backgroundColor: color,
              boxShadow: danger ? `0 0 10px ${color}` : 'none',
            }}
          />
        </div>
      </div>
    );
  };

  const renderDamageBar = (tracker) => {
    const pct = Math.max(0, Math.min(100, (tracker.value / tracker.max) * 100));
    const isInverse = tracker.id === 'ros_level'; // Higher = worse
    const isDanger = isInverse ? tracker.value > 50 : tracker.value < 40;
    const isWarning = isInverse ? tracker.value > 25 : tracker.value < 60;

    return (
      <div className={`resource-bar ${isDanger ? 'bar-danger' : isWarning ? 'bar-warning' : ''}`}
        key={tracker.id}>
        <div className="resource-bar-label">
          <span>{getName(tracker)}</span>
          <span className="resource-bar-value">{tracker.value.toFixed(1)}%</span>
        </div>
        <div className="resource-bar-track">
          <div
            className="resource-bar-fill"
            style={{
              width: `${pct}%`,
              backgroundColor: tracker.color,
              boxShadow: isDanger ? `0 0 10px ${tracker.color}` : 'none',
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="resource-bars">
      <div className="resource-section">
        <h3>Cell Status</h3>
        {Object.values(simState.damage).map(tracker => renderDamageBar(tracker))}
      </div>

      <div className="resource-section">
        <h3>Cofactors</h3>
        {resourceMolecules.map(id => {
          const mol = simState.molecules[id];
          if (!mol) return null;
          const scenarioMol = scenario.molecules.find(m => m.id === id);
          return renderBar(
            getName(scenarioMol || mol),
            mol.amount,
            mol.maxAmount || 100,
            mol.color,
            mol.amount < (mol.maxAmount || 100) * 0.3,
            mol.amount < (mol.maxAmount || 100) * 0.1
          );
        })}
      </div>

      <div className="resource-section">
        <h3>Key Molecules</h3>
        {toxicMolecules.map(id => {
          const mol = simState.molecules[id];
          if (!mol) return null;
          const scenarioMol = scenario.molecules.find(m => m.id === id);
          return renderBar(
            getName(scenarioMol || mol),
            mol.amount,
            mol.maxAmount || 100,
            mol.color,
            id === 'NAPQI' && mol.amount > 5,
            id === 'NAPQI' && mol.amount > 15
          );
        })}
      </div>
    </div>
  );
};

export default ResourceBars;
